import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastComponent: React.FC<ToastProps> = ({ toast, onRemove }) => {
  useEffect(() => {
    if (toast.duration !== 0) { // allow 0 to mean infinite
      const timer = setTimeout(() => {
        onRemove(toast.id);
      }, toast.duration || 5000);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id, onRemove]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon className="h-6 w-6 text-emerald-400" />;
      case 'error':
        return <XCircleIcon className="h-6 w-6 text-rose-400" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-6 w-6 text-amber-400" />;
      case 'info':
        return <InformationCircleIcon className="h-6 w-6 text-sky-400" />;
      default:
        return <InformationCircleIcon className="h-6 w-6 text-white/70" />;
    }
  };

  const getThemeStyles = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-emerald-900/40 border-emerald-500/30 text-emerald-50';
      case 'error':
        return 'bg-rose-900/40 border-rose-500/30 text-rose-50';
      case 'warning':
        return 'bg-amber-900/30 border-amber-500/30 text-amber-50';
      case 'info':
      default:
        return 'bg-sky-900/40 border-sky-500/30 text-sky-50';
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 50, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
      drag="x"
      dragConstraints={{ left: 0, right: 300 }}
      dragElastic={0.7}
      onDragEnd={(e, { offset, velocity }) => {
        if (offset.x > 100 || velocity.x > 500) {
          onRemove(toast.id);
        }
      }}
      className={`
        relative w-full max-w-sm overflow-hidden
        rounded-2xl border backdrop-blur-md shadow-2xl
        p-4 pointer-events-auto touch-pan-y
        ${getThemeStyles()}
      `}
    >
      {/* Liquid glass highlight effect */}
      <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
      <div className="absolute inset-0 bg-gradient-to-tl from-white/5 to-transparent rounded-2xl pointer-events-none"></div>

      <div className="flex items-start space-x-3 relative z-10">
        <div className="flex-shrink-0 mt-0.5 shadow-sm rounded-full bg-black/20 p-1">
          {getIcon()}
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold tracking-wide">
            {toast.title}
          </h4>
          <p className="text-sm opacity-90 leading-relaxed mt-1 font-light">
            {toast.message}
          </p>

          {toast.action && (
            <button
              onClick={toast.action.onClick}
              className="mt-3 text-sm font-medium underline hover:no-underline opacity-80 hover:opacity-100 transition-opacity duration-200"
            >
              {toast.action.label}
            </button>
          )}
        </div>

        <button
          onClick={() => onRemove(toast.id)}
          className="flex-shrink-0 p-1.5 rounded-full hover:bg-white/10 transition-colors duration-200 opacity-60 hover:opacity-100"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  return (
    <div className="fixed top-4 right-4 z-[10001] flex flex-col items-end space-y-3 pointer-events-none w-full max-w-[calc(100vw-2rem)] sm:max-w-sm">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastComponent
            key={toast.id}
            toast={toast}
            onRemove={onRemove}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default ToastContainer;
